/**
 * Two-factor authentication, from the settings screen's point of view.
 *
 * The status query is the single source of truth for what the card renders;
 * every mutation invalidates it rather than patching local state, so the card
 * cannot drift from the server about whether the account is protected.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { twoFactorApi } from '../services/api/auth';

const STATUS_KEY = ['auth', 'two-factor', 'status'] as const;

export function useTwoFactorStatus() {
  return useQuery({ queryKey: STATUS_KEY, queryFn: () => twoFactorApi.status() });
}

/** Generate a secret to show. Nothing is enabled by this. */
export function useStartTwoFactorSetup() {
  return useMutation({ mutationFn: () => twoFactorApi.setup() });
}

/** Confirm the secret with a live code; returns the backup codes, once. */
export function useEnableTwoFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => twoFactorApi.enable(code),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STATUS_KEY });
    },
  });
}

export function useDisableTwoFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (password: string) => twoFactorApi.disable(password),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STATUS_KEY });
    },
  });
}

export function useRegenerateBackupCodes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (password: string) => twoFactorApi.regenerateBackupCodes(password),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STATUS_KEY });
    },
  });
}

/** Turn an axios rejection into the message the server actually sent. */
export const twoFactorErrorMessage = (error: unknown, fallback: string): string => {
  const response = (error as { response?: { data?: { error?: unknown } } })?.response;
  const message = response?.data?.error;
  return typeof message === 'string' && message ? message : fallback;
};
